import FormControl from "@mui/material/FormControl";
import SwitchMui from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme } from "@mui/material";
import { ACTIVATE, DEACTIVATE } from "../../i18n/keysTranslations";
import { WHITE_COLOR } from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import Loader from "../Loaders/Loader";
import { useState } from "react";

const SWITCH_WIDTH = 32;
const SWITCH_HEIGHT = 20;
const THUMB_SIZE = 14;
const THUMB_OFFSET = SWITCH_WIDTH - THUMB_SIZE - 2 * 3;
const TRACK_RADIUS = SWITCH_HEIGHT / 2;
const SWITCH_INACTIVE_COLOR = "#e2e8f0";

const getSwitchSx = (activeColor, inactiveColor) => ({
  width: SWITCH_WIDTH,
  height: SWITCH_HEIGHT,
  padding: 0,
  overflow: "visible",
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "3px",
    transitionDuration: "150ms",
    "&.Mui-checked": {
      transform: `translateX(${THUMB_OFFSET}px)`,
      "& + .MuiSwitch-track": {
        background: activeColor,
        opacity: 1,
      },
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.4,
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      boxShadow: "0 0 0 2px #fff, 0 0 0 4px rgba(148,163,184,0.6)",
    },
  },
  "& .MuiSwitch-thumb": {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    backgroundColor: WHITE_COLOR,
    boxShadow: "0 1px 2px rgba(15,23,42,0.18)",
  },
  "& .MuiSwitch-track": {
    borderRadius: TRACK_RADIUS,
    backgroundColor: inactiveColor,
    opacity: 1,
    transition: "background-color 150ms",
  },
});

const Switch = ({
  id = "",
  isActive,
  disabled = false,
  size = "medium",
  onChangeIsActive = () => {},
  hasLabel = true,
  labelActive = DEACTIVATE,
  labelDeactivate = ACTIVATE,
  colorActivate = null,
  colorDeactivated = null,
  labelPlacement = "end",
  sx = {},
  sxLabel = {},
  loading = false,
}) => {
  const theme = useTheme();
  const [isActiveState, setIsActiveState] = useState(isActive);
  const { t } = useTranslationApp();
  const onChangeSwitchActive = (e) => {
    onChangeIsActive(e.target.checked);
    setIsActiveState(e.target.checked);
  };

  if (loading) {
    return <Loader size={20} hasMarginTop={false} />;
  }

  const finalIsActive =
    typeof isActive === "undefined" ? isActiveState : isActive;
  const activeColor = colorActivate || theme.palette.button.primary.main;
  const inactiveColor = colorDeactivated || SWITCH_INACTIVE_COLOR;

  return (
    <FormControl
      disabled={disabled}
      variant="standard"
      sx={{ overflow: "visible", ...(hasLabel ? { width: "100%" } : {}), ...sx }}
    >
      <FormControlLabel
        sx={{
          display: hasLabel ? "flex" : "inline-flex",
          alignItems: "center",
          gap: hasLabel ? 1 : 0,
          overflow: "visible",
          ml: 0,
          mr: 0,
          ".MuiFormControlLabel-label": {
            fontSize: size === "medium" ? "0.9rem" : "0.8rem",
            fontWeight: 600,
          },
          ...sxLabel,
          ".MuiSwitch-root": {
            overflow: "visible",
            flexShrink: 0,
          },
          "& .MuiFormControlLabel-label": {
            minWidth: 0,
          },
        }}
        onClick={(e) => e.stopPropagation()}
        control={
          <SwitchMui
            id={id}
            size="small"
            disableRipple
            checked={finalIsActive}
            onChange={(e) => {
              e.stopPropagation();
              onChangeSwitchActive(e);
            }}
            sx={getSwitchSx(activeColor, inactiveColor)}
          />
        }
        labelPlacement={labelPlacement}
        label={
          hasLabel ? (finalIsActive ? t(labelActive) : t(labelDeactivate)) : ""
        }
      />
    </FormControl>
  );
};

export default Switch;
